import {
	createContext, memo, type ReactElement, useContext, useEffect
} from 'react'
import toast from 'react-hot-toast'
import Skeleton from 'react-loading-skeleton'

import type { Socket } from 'socket.io-client'

import { ApiCaller } from '@frontend/api'
import { captureException } from '@sentry/core'
import type { AnsweringStep, ChatMessage, FeedbackObject } from '@types'
import BotThinking from '@ui/chat/modules/BotThinking'
import { useMessageSocket } from '@utils/frontend'

import { useChatContext } from '../ChatProvider'
import MessageContent from '../components/message/MessageContent'
import MessageFooter from '../components/message/MessageFooter'
import type { ReactPdfViewerParams } from './ReactPdfViewer'

interface MessageProps {
	readonly chatId: string;
	readonly socket: Socket;
	readonly message: ChatMessage;
	readonly children?: ReactElement | undefined;
	readonly userName?: string | undefined;
	readonly isReasoning?: boolean | undefined;
	readonly isLastMessage?: boolean | undefined;
	readonly onSourceSelected?: ((params: ReactPdfViewerParams) => void) | undefined;
	readonly onFollowUp?: (followUp: string, isReasoning: boolean) => Promise<void> | undefined;
	readonly onFeedback?: (feedback: FeedbackObject) => Promise<void> | undefined;
	readonly onRephrase?: () => Promise<void> | undefined;
	readonly onRetry?: () => Promise<void> | undefined;
	readonly setIsSidebarCollapsed?: (isSidebarCollapsed: boolean) => void | undefined;
	readonly isSidebarCollapsed?: boolean | undefined;
}

interface IMessageContext {
	readonly children?: ReactElement | undefined;
	readonly message: ChatMessage;
	readonly userName?: string | undefined;
	readonly isReasoning?: boolean | undefined;
	readonly isLastMessage?: boolean | undefined;
	readonly answeringStep?: AnsweringStep | undefined;
	readonly isWritting?: boolean | undefined;
	readonly onSourceSelected?: ((params: ReactPdfViewerParams) => void) | undefined;
	readonly onFollowUp?: (followUp: string, isReasoning: boolean) => Promise<void> | undefined;
	readonly onFeedback?: (feedback: FeedbackObject) => Promise<void> | undefined;
	readonly onRephrase?: () => Promise<void> | undefined;
	readonly onRetry?: () => Promise<void> | undefined;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const MessageContext = createContext<IMessageContext | undefined>(undefined)

function Message({
	children,
	message,
	userName,
	isReasoning,
	isLastMessage,
	socket,
	chatId,
	onFollowUp,
	onFeedback,
	onRephrase,
	onSourceSelected,
	onRetry,
	setIsSidebarCollapsed,
	isSidebarCollapsed
}: MessageProps) {
	const { chatDispatch, chatState, type } = useChatContext()

	const socketResponse = useMessageSocket({ chatId, socket, message })
	let isWritting = false

	// eslint-disable-next-line func-names, complexity
	useEffect(function updateContent() {
		if (!chatId || !message.id) {
			return
		}

		if (socketResponse) {
			if (socketResponse.artifacts && socketResponse.loadingArtifact) {
				const { loadingArtifact } = socketResponse

				if (!chatState.artifactParams
					|| chatState.artifactParams.artifact.id !== socketResponse.loadingArtifact.id
					|| chatState.artifactParams.artifact.name !== socketResponse.loadingArtifact.name
				) {
					chatDispatch({ type: 'setArtifactParams', artifactParams: { artifact: { id: loadingArtifact.id, name: loadingArtifact.name }, artifactMessage: socketResponse } })

					if (!isSidebarCollapsed) {
						setIsSidebarCollapsed?.(true)
					}
				}
			}

			if (!(chatState.loadingMessageId === socketResponse.id)) {
				chatDispatch({ type: 'setLoadingMessageId', loadingMessageId: socketResponse.id })
			}

			if (!chatState.isThinkingOrAnswering && (socketResponse.isThinking === true || socketResponse.isAnswering === true)) {
				chatDispatch({ type: 'setIsThinkingOrAnswering', isThinkingOrAnswering: true })
			}

			if (socketResponse.isAnswering || socketResponse.isThinking) {
				isWritting = true
			}

			if (socketResponse.error) {
				if (type === 'chat') {
					ApiCaller.track('Chat_message_sent_error')
				}

				captureException(socketResponse.error)

				const toastMessage = socketResponse.error?.message ?? 'We are really sorry, our servers are overloaded and we could not process your request. Please try again.'

				toast.error(<b>{ toastMessage }</b>)
				if (chatState.messages.length) {
					chatDispatch({ type: 'updateMessage', message: { id: chatState.messages[chatState.messages.length - 2]?.id, status: 'failed' } })
				}

				chatDispatch({ type: 'deleteMessage', messageId: socketResponse.id })
			}
		}
	}, [socketResponse])

	if (!chatId || !message.id) {
		return
	}

	return (
		<MessageContext.Provider
			value={ {
				isReasoning, children, message: socketResponse, userName, isWritting, isLastMessage, onFollowUp, onFeedback,
				onRephrase, onSourceSelected, onRetry
			} }>
			<div className="message w-full text-gray-800 mt-6">
				<div className={ `relative max-w-2xl xl:max-w-3xl ${message.role === 'user' ? 'pt-3' : 'pb-3'} px-3 m-auto` }>
					<div className="relative flex flex-row gap-1 md:gap-3">
						<div
							className={ `w-full relative flex flex-row gap-1 md:gap-3 ${message.role === 'user' ? 'bg-blue-100 rounded-lg p-1 px-8 pb-3' : ''} 
							${type === 'chatbot' && message.role === 'user' ? 'p-3' : ''} ` }>
							<MessageContent />
						</div>
					</div>
					{ socketResponse.isThinking && socketResponse.role === 'assistant'
						? type === 'chat'
							? (
								<BotThinking answeringStep={ socketResponse.answeringStep } type="skeletton" />
							)
							: (
								<div className="w-full mt-2">
									<Skeleton className="hiden show-1" count={ 3 } />
									<Skeleton className="w-3/4 hiden show-2" count={ 1 } />
									<Skeleton className="hiden show-3" count={ 1 } />
								</div>
							)
						: (undefined) }
					<MessageFooter />
				</div>
			</div>
		</MessageContext.Provider>
	)
}

export function useMessageContext() {
	const context = useContext(MessageContext)

	if (!context) {
		throw new Error('useMessageContext must be used within a MessageProvider')
	}

	return context
}

export default memo(Message)
